// Interventions (M12, lot L3-A, H5-04, H5-05) : signalements (portail D-042, guichet, agent), qualification
// (recevable, orienté vers un organisme, rejeté, doublon d'un principal), interventions issues d'un
// signalement recevable ou programmées, missions affectées à une équipe et à un agent (version
// incrémentée à chaque changement serveur, D-041), rapports avant, après et final, équipes municipales
// ou de prestataire (D-043), filtres, catégories, couches carte (D-047). Numéros SIG et INT (D-049).
import { z } from 'zod';
import { schemaBbox } from './dossiers';
import { schemaPosition } from './marches-structure';
import { schemaCodeCollectivite } from './usagers-comptes';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');

// Référentiels ---------------------------------------------------------------------------------

export const CATEGORIES_SIGNALEMENT = {
  eclairage: 'Éclairage public',
  voirie: 'Voirie et chaussée',
  depot_sauvage: 'Dépôt sauvage',
  assainissement: 'Assainissement et eaux',
  espaces_verts: 'Espaces verts et arbres',
  mobilier_urbain: 'Mobilier urbain',
  signalisation: 'Signalisation',
  batiment: 'Bâtiment de la collectivité',
  autre: 'Autre',
} as const;
export type CategorieSignalement = keyof typeof CATEGORIES_SIGNALEMENT;
export const CODES_CATEGORIES_SIGNALEMENT = Object.keys(CATEGORIES_SIGNALEMENT) as [CategorieSignalement, ...CategorieSignalement[]];

export const QUALIFICATIONS_SIGNALEMENT = ['a_qualifier', 'recevable', 'oriente', 'rejete', 'doublon'] as const;
export type QualificationSignalement = (typeof QUALIFICATIONS_SIGNALEMENT)[number];
export const LIBELLES_QUALIFICATION: Record<QualificationSignalement, string> = {
  a_qualifier: 'À qualifier',
  recevable: 'Recevable',
  oriente: 'Orienté vers un organisme',
  rejete: 'Rejeté',
  doublon: 'Doublon',
};

export const AUTEURS_SIGNALEMENT = ['usager', 'agent', 'guichet'] as const;
export type AuteurSignalement = (typeof AUTEURS_SIGNALEMENT)[number];

export const PRIORITES_INTERVENTION = { basse: 'Basse', normale: 'Normale', haute: 'Haute', urgente: 'Urgente' } as const;
export type PrioriteIntervention = keyof typeof PRIORITES_INTERVENTION;
export const CODES_PRIORITES_INTERVENTION = Object.keys(PRIORITES_INTERVENTION) as [PrioriteIntervention, ...PrioriteIntervention[]];

export const ORIGINES_INTERVENTION = ['signalement', 'programme'] as const;
export type OrigineIntervention = (typeof ORIGINES_INTERVENTION)[number];

/** États du workflow « intervention » (définition nationale posée par la migration 0071). */
export const ETATS_INTERVENTION = ['nouvelle', 'planifiee', 'en_cours', 'a_valider', 'validee', 'cloturee', 'annulee'] as const;
export type EtatIntervention = (typeof ETATS_INTERVENTION)[number];
export const LIBELLES_ETAT_INTERVENTION: Record<EtatIntervention, string> = {
  nouvelle: 'Nouvelle',
  planifiee: 'Planifiée',
  en_cours: 'En cours',
  a_valider: 'À valider',
  validee: 'Validée',
  cloturee: 'Clôturée',
  annulee: 'Annulée',
};
/** Transitions manuelles du workflow (les transitions automatiques demarrer et soumettre sont exécutées par le serveur). */
export const TRANSITIONS_INTERVENTION = ['planifier', 'commencer', 'soumettre', 'valider', 'cloturer', 'annuler', 'refuser'] as const;

export const ETATS_MISSION = ['affectee', 'en_cours', 'rapport_recu', 'conflit', 'validee', 'annulee'] as const;
export type EtatMission = (typeof ETATS_MISSION)[number];
export const LIBELLES_ETAT_MISSION: Record<EtatMission, string> = {
  affectee: 'Affectée',
  en_cours: 'En cours',
  rapport_recu: 'Rapport reçu',
  conflit: 'Conflit de synchronisation',
  validee: 'Validée',
  annulee: 'Annulée',
};

export const TYPES_RAPPORT_MISSION = ['avant', 'apres', 'final'] as const;
export type TypeRapportMission = (typeof TYPES_RAPPORT_MISSION)[number];
export const LIBELLES_TYPE_RAPPORT: Record<TypeRapportMission, string> = { avant: 'Avant travaux', apres: 'Après travaux', final: 'Rapport final' };

export const ETATS_RAPPORT_MISSION = ['synchronise', 'conflit', 'retenu', 'ecarte'] as const;
export type EtatRapportMission = (typeof ETATS_RAPPORT_MISSION)[number];

export const TYPES_EQUIPE = ['municipale', 'prestataire'] as const;
export type TypeEquipe = (typeof TYPES_EQUIPE)[number];
export const ROLES_MEMBRE_EQUIPE = ['chef', 'membre'] as const;

/** Statuts visibles par l'usager dans le portail (suivi d'un signalement), dérivés de la qualification et de l'intervention. */
export const STATUTS_SIGNALEMENT_USAGER = {
  recu: 'Reçu, en attente de qualification',
  pris_en_compte: 'Pris en compte, intervention à planifier',
  planifie: 'Intervention planifiée',
  en_cours: 'Intervention en cours',
  termine: 'Intervention terminée',
  oriente: 'Transmis à un organisme compétent',
  rejete: 'Non retenu',
  doublon: 'Déjà signalé, suivi avec le signalement principal',
} as const;
export type StatutSignalementUsager = keyof typeof STATUTS_SIGNALEMENT_USAGER;

const code = z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,29}$/, 'Code de 1 à 30 caractères (lettres, chiffres, point, tiret, souligné)');
const montant = z.number().int().nonnegative().max(1_000_000_000_000);

// Équipes et prestataires ----------------------------------------------------------------------

export const schemaCreationPrestataire = z.strictObject({
  /** Absent : généré à partir des initiales du nom (D-038, D-049). */
  code: code.optional(),
  nom: z.string().trim().min(2).max(200),
  contact: z.string().trim().max(300).optional(),
  contrat_reference: z.string().trim().max(100).optional(),
});
export const schemaModificationPrestataire = z
  .strictObject({ nom: z.string().trim().min(2).max(200), contact: z.string().trim().max(300).nullable(), contrat_reference: z.string().trim().max(100).nullable(), actif: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier');

export const schemaMembreEquipe = z.strictObject({ appartenance_id: uuid, role: z.enum(ROLES_MEMBRE_EQUIPE).default('membre') });

export const schemaCreationEquipe = z
  .strictObject({
    code: code.optional(),
    nom: z.string().trim().min(2).max(120),
    type: z.enum(TYPES_EQUIPE),
    prestataire_id: uuid.optional(),
    responsable_appartenance_id: uuid.optional(),
    service_id: uuid.optional(),
    membres: z.array(schemaMembreEquipe).max(50).default([]),
  })
  .refine((v) => (v.type === 'prestataire') === Boolean(v.prestataire_id), { message: 'Une équipe de prestataire désigne son prestataire, une équipe municipale n\'en a pas', path: ['prestataire_id'] });

export const schemaModificationEquipe = z
  .strictObject({
    nom: z.string().trim().min(2).max(120),
    responsable_appartenance_id: uuid.nullable(),
    service_id: uuid.nullable(),
    actif: z.boolean(),
    /** Liste complète des membres : les absents sont retirés (fin posée), les nouveaux ajoutés. */
    membres: z.array(schemaMembreEquipe).max(50),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier');

export const schemaFiltreEquipes = z.strictObject({
  type: z.enum(TYPES_EQUIPE).optional(),
  actif: z.enum(['oui', 'non']).optional(),
});

// Signalements ---------------------------------------------------------------------------------

/** Dépôt par l'usager du portail (POST /usager/signalements) ; les photos suivent, une par requête (POST /usager/signalements/:id/photos). */
export const schemaDepotSignalementUsager = z.strictObject({
  collectivite: schemaCodeCollectivite,
  categorie: z.enum(CODES_CATEGORIES_SIGNALEMENT),
  description: z.string().trim().min(3).max(5000),
  position: schemaPosition,
  adresse: z.string().trim().max(300).optional(),
});

/** Dépôt par un agent (constat sur le terrain) ou au guichet pour un tiers (POST /signalements). */
export const schemaCreationSignalementAgent = z
  .strictObject({
    categorie: z.enum(CODES_CATEGORIES_SIGNALEMENT),
    description: z.string().trim().min(3).max(5000),
    position: schemaPosition.optional(),
    adresse: z.string().trim().max(300).optional(),
    auteur_type: z.enum(['agent', 'guichet']).default('agent'),
    /** Guichet : usager au compte du portail (notifié), ou tiers sans compte. */
    usager_compte_id: uuid.optional(),
    tiers_nom: z.string().trim().max(200).optional(),
    tiers_telephone: z.string().trim().max(40).optional(),
    service_id: uuid.optional(),
  })
  .refine((v) => v.position || v.adresse, { message: 'Indiquer une position ou une adresse', path: ['position'] });

const motif = z.string().trim().min(3).max(1000);

/** Qualification par le chef de service (ou le responsable technique) : chaque branche a ses champs. */
export const schemaQualificationSignalement = z.discriminatedUnion('qualification', [
  z.strictObject({
    qualification: z.literal('recevable'),
    service_id: uuid.optional(),
    /** Intervention créée dans le même acte (défaut) avec cette priorité et ce titre. */
    creer_intervention: z.boolean().default(true),
    priorite: z.enum(CODES_PRIORITES_INTERVENTION).default('normale'),
    titre: z.string().trim().min(3).max(200).optional(),
    motif: z.string().trim().max(1000).optional(),
  }),
  z.strictObject({ qualification: z.literal('oriente'), organisme_oriente: z.string().trim().min(2).max(200), motif: z.string().trim().max(1000).optional() }),
  z.strictObject({ qualification: z.literal('rejete'), motif }),
  z.strictObject({ qualification: z.literal('doublon'), principal_id: uuid, motif: z.string().trim().max(1000).optional() }),
]);
export type QualificationSignalementEntree = z.infer<typeof schemaQualificationSignalement>;

export const schemaFiltreSignalements = z.strictObject({
  qualification: z.enum(QUALIFICATIONS_SIGNALEMENT).optional(),
  categorie: z.enum(CODES_CATEGORIES_SIGNALEMENT).optional(),
  auteur_type: z.enum(AUTEURS_SIGNALEMENT).optional(),
  service: uuid.optional(),
  bbox: schemaBbox.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(200),
});

// Interventions --------------------------------------------------------------------------------

export const schemaCreationIntervention = z
  .strictObject({
    titre: z.string().trim().min(3).max(200),
    categorie: z.enum(CODES_CATEGORIES_SIGNALEMENT),
    description: z.string().trim().max(5000).optional(),
    priorite: z.enum(CODES_PRIORITES_INTERVENTION).default('normale'),
    /** Intervention issue d'un signalement recevable sans intervention, ou programmée. */
    signalement_id: uuid.optional(),
    service_id: uuid.optional(),
    equipe_id: uuid.optional(),
    position: schemaPosition.optional(),
    adresse: z.string().trim().max(300).optional(),
    planifiee_le: jour.optional(),
    cout_estime: montant.optional(),
  })
  .refine((v) => v.signalement_id || v.position || v.adresse, { message: 'Indiquer une position, une adresse ou un signalement', path: ['position'] });

export const schemaModificationIntervention = z
  .strictObject({
    titre: z.string().trim().min(3).max(200),
    description: z.string().trim().max(5000).nullable(),
    priorite: z.enum(CODES_PRIORITES_INTERVENTION),
    service_id: uuid.nullable(),
    equipe_id: uuid.nullable(),
    position: schemaPosition.nullable(),
    adresse: z.string().trim().max(300).nullable(),
    planifiee_le: jour.nullable(),
    cout_estime: montant.nullable(),
    cout_reel: montant.nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier');

/** Transition manuelle du workflow (POST /interventions/:id/transitions/:code). */
export const schemaTransitionIntervention = z.strictObject({
  motif: z.string().trim().max(2000).optional(),
  /** Planification : date retenue. */
  planifiee_le: jour.optional(),
  /** Clôture : coût réel constaté. */
  cout_reel: montant.optional(),
});

export const schemaFiltreInterventions = z.strictObject({
  etat: z.enum(ETATS_INTERVENTION).optional(),
  priorite: z.enum(CODES_PRIORITES_INTERVENTION).optional(),
  categorie: z.enum(CODES_CATEGORIES_SIGNALEMENT).optional(),
  origine: z.enum(ORIGINES_INTERVENTION).optional(),
  service: uuid.optional(),
  equipe: uuid.optional(),
  /** en_cours : états non terminaux (nouvelle à validée). */
  en_cours: z.enum(['oui', 'non']).optional(),
  bbox: schemaBbox.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(200),
});

// Missions et rapports -------------------------------------------------------------------------

export const schemaCreationMission = z.strictObject({
  equipe_id: uuid,
  /** Agent de l'équipe chargé de la mission ; absent : toute l'équipe (le chef d'équipe est notifié). */
  agent_appartenance_id: uuid.optional(),
  date: jour,
  consignes: z.string().trim().max(5000).optional(),
});

/** Réaffectation ou modification par le responsable : la version serveur de la mission augmente de un. */
export const schemaModificationMission = z
  .strictObject({
    equipe_id: uuid,
    agent_appartenance_id: uuid.nullable(),
    date: jour,
    consignes: z.string().trim().max(5000).nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier');

export const schemaAnnulationMission = z.strictObject({ motif: motif });

export const schemaMateriauRapport = z.strictObject({
  designation: z.string().trim().min(1).max(200),
  /** Quantité saisie ; absente quand l'agent n'en a pas indiqué (saisie libre du terrain, D-132). */
  quantite: z.number().nonnegative().max(1_000_000).optional(),
  unite: z.string().trim().max(20).optional(),
});

/** Contenu d'un rapport de mission (json : constats, travaux, matériaux, durée). */
export const schemaContenuRapportMission = z.strictObject({
  constats: z.string().trim().max(5000).default(''),
  travaux: z.string().trim().max(5000).default(''),
  materiaux: z.array(schemaMateriauRapport).max(100).default([]),
  duree_minutes: z.number().int().nonnegative().max(100_000).optional(),
  /** Terminé : la mission est achevée (rapport final). */
  termine: z.boolean().default(false),
});
export type ContenuRapportMission = z.infer<typeof schemaContenuRapportMission>;

/** Rapport saisi en ligne (POST /interventions/missions/:id/rapports) : même traitement qu'une opération synchronisée. */
export const schemaCreationRapportMission = z.strictObject({
  type: z.enum(TYPES_RAPPORT_MISSION),
  contenu: schemaContenuRapportMission,
  /** Identifiant client facultatif (idempotence) ; généré par le serveur sinon. */
  id_client: uuid.optional(),
  /** Version de la mission connue du client ; absente : version courante (aucun conflit possible). */
  version_attendue: z.number().int().nonnegative().optional(),
  enregistre_le: z.iso.datetime({ offset: true }).optional(),
});

// Carte (D-047) --------------------------------------------------------------------------------

export const schemaFiltreCarteSignalements = z.strictObject({
  bbox: schemaBbox.optional(),
  qualification: z.enum(QUALIFICATIONS_SIGNALEMENT).optional(),
  categorie: z.enum(CODES_CATEGORIES_SIGNALEMENT).optional(),
  limite: z.coerce.number().int().min(1).max(2000).default(500),
});
export const schemaFiltreCarteInterventions = z.strictObject({
  bbox: schemaBbox.optional(),
  etat: z.enum(ETATS_INTERVENTION).optional(),
  en_cours: z.enum(['oui', 'non']).optional(),
  limite: z.coerce.number().int().min(1).max(2000).default(500),
});

/** Propriétés publiées sur la carte : aucune donnée personnelle (ni auteur, ni agent). */
export interface ProprietesCarteSignalement {
  objet: 'signalement';
  id: string;
  numero: string;
  categorie: CategorieSignalement;
  categorie_libelle: string;
  qualification: QualificationSignalement;
  cree_le: string;
}
export interface ProprietesCarteIntervention {
  objet: 'intervention';
  id: string;
  numero: string;
  titre: string;
  categorie: CategorieSignalement;
  priorite: PrioriteIntervention;
  etat: EtatIntervention;
  planifiee_le: string | null;
}

/** Statut usager d'un signalement d'après sa qualification et l'état de son intervention. */
export function statutSignalementUsager(qualification: QualificationSignalement, etatIntervention: EtatIntervention | null): StatutSignalementUsager {
  if (qualification === 'a_qualifier') return 'recu';
  if (qualification === 'oriente') return 'oriente';
  if (qualification === 'rejete') return 'rejete';
  if (qualification === 'doublon') return 'doublon';
  if (!etatIntervention || etatIntervention === 'nouvelle') return 'pris_en_compte';
  if (etatIntervention === 'planifiee') return 'planifie';
  if (etatIntervention === 'en_cours' || etatIntervention === 'a_valider' || etatIntervention === 'validee') return 'en_cours';
  if (etatIntervention === 'annulee') return 'rejete';
  return 'termine';
}
